import React, { useEffect, useState } from 'react';
import { priceTooltipText } from '../../utils/number';
import PriceCostBasisHint from '../../components/PriceCostBasisHint';

interface Props {
 listing: {
  listing_hash: string;
  mo_family_hash?: string;
  title: string;
  description?: string | null;
  period_start: string;
  period_end: string;
  price_per_impression: string | number;
  industry_ksic_major?: string | null;
  region_sido?: string | null;
  region_gugun?: string | null;
  daily_budget_cap?: string | number | null;
  guaranteed_impressions?: number | null;
  is_active: boolean;
  side: string;
 };
 onClose: () => void;
 onSaved: () => void;
}

const ListingEditModal: React.FC<Props> = ({ listing, onClose, onSaved }) => {
 const [title, setTitle] = useState(listing.title);
 const [description, setDescription] = useState(listing.description || '');
 const [periodStart, setPeriodStart] = useState(String(listing.period_start).slice(0, 10));
 const [periodEnd, setPeriodEnd] = useState(String(listing.period_end).slice(0, 10));
 const [price, setPrice] = useState(String(listing.price_per_impression));
 const [dailyBudgetCap, setDailyBudgetCap] = useState(
  listing.daily_budget_cap != null ? String(listing.daily_budget_cap) : ''
 );
 const [guaranteedImpressions, setGuaranteedImpressions] = useState(
  listing.guaranteed_impressions != null ? String(listing.guaranteed_impressions) : ''
 );
 const [industryKsicMajor, setIndustryKsicMajor] = useState(listing.industry_ksic_major || '');
 const [regionSido, setRegionSido] = useState(listing.region_sido || '');
 const [regionGugun, setRegionGugun] = useState(listing.region_gugun || '');
 const [isActive, setIsActive] = useState(listing.is_active);
 const [error, setError] = useState<string | null>(null);
 const [saving, setSaving] = useState(false);
 const [deleting, setDeleting] = useState(false);
 const [industries, setIndustries] = useState<{ code: string; ko_name: string; group_name?: string }[]>([]);
 const [regions, setRegions] = useState<{ sido: string; gugun: string }[]>([]);

 useEffect(() => {
  fetch('/market/listings/meta/industries').then(r => r.json()).then(j => {
   const list: { code: string; ko_name: string; group_name?: string }[] = j.data || [];
   setIndustries(list);
   // legacy KSIC 값 등 새 분류체계에 없는 코드는 자동으로 비워 저장 시 NULL 로 정리
   if (industryKsicMajor && !list.some(i => i.code === industryKsicMajor)) {
    setIndustryKsicMajor('');
   }
  });
  fetch('/market/listings/meta/regions').then(r => r.json()).then(j => setRegions(j.data || []));
 }, []);

 const sidos = [...new Set(regions.map(r => r.sido))];
 const guguns = regionSido ? regions.filter(r => r.sido === regionSido).map(r => r.gugun) : [];

 const isPriceField = listing.side !== 'seeking_member' && listing.side !== 'barter';

 const save = async () => {
  setSaving(true);
  setError(null);
  try {
   const body: any = {
    title: title.trim(),
    description: description.trim() || null,
    period_start: periodStart,
    period_end: periodEnd,
    industry_ksic_major: industryKsicMajor.trim() || null,
    region_sido: regionSido.trim() || null,
    region_gugun: regionGugun.trim() || null,
    is_active: isActive,
   };
   if (isPriceField) {
    body.price_per_impression = Number(price);
    body.daily_budget_cap = dailyBudgetCap ? Number(dailyBudgetCap) : null;
    body.guaranteed_impressions = guaranteedImpressions ? Number(guaranteedImpressions) : null;
   }
   const r = await fetch(`/market/listings/${listing.listing_hash}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
   });
   const j = await r.json();
   if (!r.ok || !j.success) throw new Error(j.error || '수정 실패');
   onSaved();
  } catch (err: any) {
   setError(err.message || '수정 실패');
  } finally {
   setSaving(false);
  }
 };

 const remove = async () => {
  if (!confirm('이 매물을 정말 삭제하시겠습니까?\n진행 중인 제안에는 영향이 없습니다.')) return;
  setDeleting(true);
  setError(null);
  try {
   const r = await fetch(`/market/listings/${listing.listing_hash}`, {
    method: 'DELETE',
    credentials: 'include',
   });
   const j = await r.json();
   if (!r.ok || !j.success) throw new Error(j.error || '삭제 실패');
   onSaved();
  } catch (err: any) {
   setError(err.message || '삭제 실패');
   setDeleting(false);
  }
 };

 return (
  <div
   className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
   onClick={(e) => { if (e.target === e.currentTarget && !saving && !deleting) onClose(); }}
  >
   <div className="bg-white p-3 md:p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
    <div className="flex justify-between items-center mb-3">
     <h2 className="text-lg md:text-xl font-bold">매물 수정</h2>
     <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
    </div>

    <div className="space-y-3 text-sm">
     <div>
      <label className="block text-gray-600 mb-1">제목 *</label>
      <input
       type="text"
       value={title}
       onChange={(e) => setTitle(e.target.value)}
       maxLength={120}
       className="w-full px-2 py-1 border border-gray-300"
      />
     </div>
     <div>
      <label className="block text-gray-600 mb-1">설명</label>
      <textarea
       value={description}
       onChange={(e) => setDescription(e.target.value)}
       rows={3}
       className="w-full px-2 py-1 border border-gray-300"
      />
     </div>
     <div className="grid grid-cols-2 gap-2">
      <div>
       <label className="block text-gray-600 mb-1">기간 시작 *</label>
       <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
      </div>
      <div>
       <label className="block text-gray-600 mb-1">기간 종료 *</label>
       <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
      </div>
     </div>
     {isPriceField && (
      <>
       <div>
        <label className="block text-gray-600 mb-1">
         단가 (원/회){' '}
         <span className="text-gray-400 cursor-help" title={priceTooltipText(Number(price))}>ⓘ</span>
        </label>
        <input
         type="number" step="0.1" min="0"
         value={price} onChange={e => setPrice(e.target.value)}
         className="w-32 px-2 py-1 border border-gray-300"
        />
        {listing.mo_family_hash && (
         <div className="mt-1">
          <PriceCostBasisHint moFamilyHash={listing.mo_family_hash} pricePerImpression={Number(price)} />
         </div>
        )}
       </div>
       <div className="grid grid-cols-2 gap-2">
        <div>
         <label className="block text-gray-600 mb-1">
          하루 최대 책정 금액 (원){' '}
          <span
           className="text-gray-400 cursor-help"
           title={'광고주 안전 장치 — 그 날 송출이 캡을 넘어도 청구는 캡까지만.\n송출은 계속됩니다 (잉여분은 미디어사 무상 노출).'}
          >ⓘ</span>
         </label>
         <input
          type="number" step="100" min="0"
          value={dailyBudgetCap} onChange={e => setDailyBudgetCap(e.target.value)}
          placeholder="(미지정 = 한도 없음)"
          className="w-full px-2 py-1 border border-gray-300"
         />
        </div>
        <div>
         <label className="block text-gray-600 mb-1">
          송출 횟수 보장 (전 기간){' '}
          <span
           className="text-gray-400 cursor-help"
           title={'기간 종료 시점 보장치 대비:\n· 50% 미만 송출 → 전액 환불\n· 50~100% 구간 → 실제 송출 비율로 비례 청구\n· 100% 이상 → 보장치 × 단가 정액 청구'}
          >ⓘ</span>
         </label>
         <input
          type="number" step="100" min="0"
          value={guaranteedImpressions} onChange={e => setGuaranteedImpressions(e.target.value)}
          placeholder="(미지정 = 보장 없음)"
          className="w-full px-2 py-1 border border-gray-300"
         />
        </div>
       </div>
      </>
     )}
     <div>
      <label className="block text-gray-600 mb-1">
       매장 분류{' '}
       <span
        className="text-gray-400 cursor-help"
        title={'스크린이 설치된 장소의 업종 (또는 광고주의 사업 분류). 광고주·미디어 매칭 정확도를 높이기 위해 대분류로 선택.'}
       >ⓘ</span>
      </label>
      <select
       value={industryKsicMajor}
       onChange={(e) => setIndustryKsicMajor(e.target.value)}
       className="w-full px-2 py-1 border border-gray-300"
      >
       <option value="">선택 안 함</option>
       {Object.entries(industries.reduce((acc, i) => {
        const g = i.group_name || '기타';
        (acc[g] ||= []).push(i);
        return acc;
       }, {} as Record<string, typeof industries>)).map(([group, items]) => (
        <optgroup key={group} label={group}>
         {items.map(i => <option key={i.code} value={i.code}>{i.ko_name}</option>)}
        </optgroup>
       ))}
      </select>
     </div>
     <div className="grid grid-cols-2 gap-2">
      <div>
       <label className="block text-gray-600 mb-1">지역 시·도</label>
       <select
        value={regionSido}
        onChange={(e) => { setRegionSido(e.target.value); setRegionGugun(''); }}
        className="w-full px-2 py-1 border border-gray-300"
       >
        <option value="">선택 안 함</option>
        {sidos.map(s => <option key={s} value={s}>{s}</option>)}
       </select>
      </div>
      <div>
       <label className="block text-gray-600 mb-1">지역 군·구</label>
       <select
        value={regionGugun}
        onChange={(e) => setRegionGugun(e.target.value)}
        disabled={!regionSido}
        className="w-full px-2 py-1 border border-gray-300 disabled:opacity-50"
       >
        <option value="">선택 안 함</option>
        {guguns.map(g => <option key={g} value={g}>{g}</option>)}
       </select>
      </div>
     </div>
     <label className="flex items-center gap-2 cursor-pointer">
      <input
       type="checkbox"
       checked={isActive}
       onChange={(e) => setIsActive(e.target.checked)}
      />
      <span>활성화 (마켓 노출)</span>
     </label>
     {error && <div className="bg-red-50 text-red-700 p-2 text-xs">{error}</div>}
     <div className="flex gap-2 pt-2">
      <button
       onClick={save}
       disabled={saving || deleting || !title.trim()}
       className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
      >
       {saving ? '저장 중...' : '저장'}
      </button>
      <button
       onClick={onClose}
       disabled={saving || deleting}
       className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
      >
       취소
      </button>
      <button
       onClick={remove}
       disabled={saving || deleting}
       className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
      >
       {deleting ? '삭제 중...' : '삭제'}
      </button>
     </div>
    </div>
   </div>
  </div>
 );
};

export default ListingEditModal;
